import React, { useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { FilePdfTwoTone } from '@ant-design/icons'
import { Table } from 'antd'
import type { TablePaginationConfig } from 'antd/es/table'
import { useQuery } from '@apollo/client'
import { CONFORMITY_QUERY } from '../../../../graphql/Conformity'
import CentredLoader from '../../../../components/CentredLoader'
import type { SorterResult } from 'antd/es/table/interface'
import { ConformityTableType } from './types'
import { columns } from './config'
import UpdateModal from './UpdateModal'
import { VariablesContext } from '../../contexts/variablesContext'
import { SelectedUserContext } from '../../contexts/selectedUserContext'

const Conformities = (): JSX.Element => {
  const { t } = useTranslation()
  const { variables, setVariables, apiParameters } =
    useContext(VariablesContext)
  const { canFullAccess } = useContext(SelectedUserContext)

  const { data, loading } = useQuery(CONFORMITY_QUERY, {
    variables: { ...apiParameters() },
  })

  const [selectedConformity, setSelectedConformity] = useState<
    ConformityTableType | undefined
  >()

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter:
      | SorterResult<ConformityTableType>
      | SorterResult<ConformityTableType>[]
  ) => {
    setVariables({
      ...variables,
      pagination: { ...pagination },
      filters: { ...filters },
      sorter: { ...sorter },
    })
  }

  const kbis = (link: string): JSX.Element => (
    <a href={link} target="_blank" rel="noreferrer">
      <FilePdfTwoTone twoToneColor={!!link ? undefined : '#e84e4e'} />
    </a>
  )

  if (loading) {
    return <CentredLoader />
  }

  const conformities = data.conformitiesPage.conformities
  const total = data.conformitiesPage.pagination.total

  const dataSource: ConformityTableType[] = conformities.map(
    (conformity: any): ConformityTableType => ({
      key: conformity.domiciliation.id,
      id: conformity.id,
      domiciliationId: conformity.domiciliation.id,
      kbis: kbis(conformity.domiciliation.kbis),
      address: conformity.domiciliation.address.shortname,
      fullAddress: conformity.domiciliation.address.fullAddress,
      societyName: conformity.domiciliation.society.name,
      societyBrandName: conformity.domiciliation.society.brandName,
      firstname: conformity.domiciliation.contactDetail.firstname,
      lastname: conformity.domiciliation.contactDetail.lastname,
      legalStatus: conformity.domiciliation.society.legalStatus.name,
      societyType: t(conformity.domiciliation.society.actionType),
      clientSince: dayjs(conformity.domiciliation.clientSince).format(
        'DD/MM/YYYY'
      ),
      siren: conformity.siren,
      moderator: conformity.moderator?.email,
      previousModerator: conformity.previousModerator?.email,
      checkedAt: conformity.checkedAt
        ? dayjs(conformity.checkedAt).format('DD/MM/YYYY')
        : null,
    })
  )

  return (
    <>
      {selectedConformity && (
        <UpdateModal
          conformity={selectedConformity}
          setConformity={setSelectedConformity}
        />
      )}
      <Table
        dataSource={dataSource}
        columns={columns(t, variables, canFullAccess)}
        pagination={{ ...variables.pagination, total }}
        scroll={{ x: '100vw' }}
        onChange={handleTableChange}
        onRow={record => {
          return {
            onDoubleClick: () => {
              setSelectedConformity(record)
            },
          }
        }}
      />
    </>
  )
}

export default Conformities
